import { IId, IIsAvailable, TIdName, TNameTitle } from '@interfaces/common/common';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import {
	INetworkElementCheckIpAddressDTO,
	INetworkElementDTO,
	INetworkElementsEnableDTO,
	INetworkElementsSFTPPathsDTO,
	INetworkElementsSubscribeDTO,
	TNetworkElementAutoSwitchDTO,
} from '@interfaces/networkElements/dto/networkElements';
import {
	IConnectionLog,
	IConnectionLogNames,
	INetworkElement,
	INetworkElementAutoSwitchAvailable,
	INetworkElementsMiddleware,
	IPath,
	TReconnectionStatuses,
} from '@interfaces/networkElements/networkElements';

import { client } from './client';
import { URL } from './urls';

const {
	networkElements,
	networkElementsStatusLogs,
	networkElementsMiddleware,
} = URL;

export const getNetworkElements = (params: TDefaultDTO): Promise<IItemsResponse<INetworkElement>> => {
	return client.get(networkElements, { params });
};

export const getNetworkElement = (id: string): Promise<INetworkElement> => {
	return client.get(`/${networkElements}/${id}`);
};

export const getConnectionLogs = (params: TDefaultDTO): Promise<IItemsResponse<IConnectionLog>> => {
	return client.get(networkElementsStatusLogs, { params });
};

export const getNetworkElementsMiddleware = (params: { vendor: string }): Promise<INetworkElementsMiddleware[]> => {
	return client.get(networkElementsMiddleware, { params });
};

export const getNetworkElementsTypes = (): Promise<TNameTitle[]> => {
	return client.get(`${networkElements}/types`);
};

export const getVendorsByType = (params: { type: string }): Promise<TNameTitle[]> => {
	return client.get(`${networkElements}/vendors`, { params });
};

export const getNetworkElementsConnectionStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${networkElements}/connection-status`);
};

export const getNetworkElementsEstablishConnections = (): Promise<TNameTitle[]> => {
	return client.get(`${networkElements}/establish-connection`);
};

export const getNetworkElementsSubscriptions = (): Promise<TNameTitle[]> => {
	return client.get(`${networkElements}/subscription`);
};

export const getConnectionLogsReconnectionStatuses = (): Promise<TReconnectionStatuses[]> => {
	return client.get(`/${networkElementsStatusLogs}/reconnection-statuses`);
};

export const getConnectionLogsEventTypes = (): Promise<TNameTitle[]> => {
	return client.get(`/${networkElementsStatusLogs}/event-types`);
};

export const getConnectionLogsNames = (): Promise<IConnectionLogNames[]> => {
	return client.get(`/${networkElementsStatusLogs}/names`);
};

export const getConnectionLogsVendors = (): Promise<TNameTitle[]> => {
	return client.get(`/${networkElementsStatusLogs}/vendors`);
};

export const getNetworkElementsCheckName = (dto: TIdName): Promise<boolean> => {
	return client.post(`${networkElements}/check-name`, dto);
};

export const getNetworkElementsCheckPath = ({ id, ...dto }: INetworkElementsSFTPPathsDTO): Promise<boolean> => {
	return client.post(`${networkElements}/${id}/check-paths`, dto);
};

export const getNetworkElementsCheckIpAddress = (dto: INetworkElementCheckIpAddressDTO): Promise<boolean> => {
	return client.post(`${networkElements}/check-ip-address`, dto);
};

export const createNetworkElement = (dto: INetworkElementDTO): Promise<void> => {
	return client.post(`/${networkElements}/create`, dto);
};

export const editNetworkElement = ({ id, ...dto }: INetworkElementsEnableDTO): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/update`, dto);
};

export const deleteNetworkElement = (id: string): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/delete`, {});
};

export const subscribeNetworkElement = ({ id, ...dto }: INetworkElementsSubscribeDTO): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/subscribe`, dto);
};

export const connectNetworkElement = ({ id, ...dto }: INetworkElementsEnableDTO): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/enable`, dto);
};

export const enableSFTPNetworkElement = ({ id, ...dto }: INetworkElementsEnableDTO): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/enable-direct-sftp`, dto);
};

export const disableSFTPNetworkElement = ({ id, ...dto }: INetworkElementsEnableDTO): Promise<INetworkElement> => {
	return client.post(`/${networkElements}/${id}/disable-direct-sftp`, dto);
};

export const getNetworkElementVesCuPath = ({ id }: IId): Promise<IPath> => {
	return client.get(`/${networkElements}/${id}/get-cu-path-from-ves`);
};

export const getNetworkElementVesDuPath = ({ id }: IId): Promise<IPath> => {
	return client.get(`/${networkElements}/${id}/get-du-path-from-ves`);
};

export const getNetworkElementCheckVesCu = (id: string): Promise<IIsAvailable> => {
	return client.get(`/${networkElements}/${id}/check-cu-path-from-ves`);
};

export const getAutoSwitchAvailable = (id: string): Promise<INetworkElementAutoSwitchAvailable> => {
	return client.get(`/${networkElements}/${id}/check-auto-switch`);
};

export const getNetworkElementCheckVesDu = (id: string): Promise<IIsAvailable> => {
	return client.get(`/${networkElements}/${id}/check-du-path-from-ves`);
};

export const enableNetworkElementAutoSwitch = ({
	id,
	...dto
}: TNetworkElementAutoSwitchDTO): Promise<void> => {
	return client.post(`/${networkElements}/${id}/enable-auto-switch`, dto);
};

export const disableNetworkElementAutoSwitch = ({ id }: IId): Promise<void> => {
	return client.post(`/${networkElements}/${id}/disable-auto-switch`, {});
};

export const editNetworkElementAutoSwitch = ({
	id,
	...dto
}: TNetworkElementAutoSwitchDTO): Promise<void> => {
	return client.post(`/${networkElements}/${id}/edit-auto-switch`, dto);
};
